<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useQuery } from '@tanstack/vue-query';
import { routePlansApi, type RoutePlan } from '@/api/route-plans';
import { patientsApi } from '@/api/patients';
import { skillsApi } from '@/api/skills';
import {
  requestErrorMessage, blockedProfessionalPayload, type BlockedProfessionalPayload,
} from '@/utils/api-errors';
import { useTableFilter } from '@/composables/useTableFilter';
import ResponsiveDialog from '@/components/responsive-dialog.vue';
import TimeInput from '@/components/time-input.vue';
import RouteBlockedConfirmDialog from './route-blocked-confirm-dialog.vue';
import { windowFitsDuration } from './route-plan-setup.utils';

// "+ Agregar visita": creates a PENDING visit (full requirement fields, mirroring the setup
// stepper's visit row). It lands in the left panel; placement happens by drag/click afterwards.
// With the prefill props (empty-slot click and no pendings) it opens in "hora fija" mode at the
// clicked time and, on save, the visit is created AND placed on that caregiver directly.
interface Props {
  modelValue: boolean;
  routePlan: RoutePlan;
  prefillCaregiverId?: number | null;
  prefillStartTime?: string | null;
}

const props = withDefaults(defineProps<Props>(), {
  prefillCaregiverId: null,
  prefillStartTime: null,
});
const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  'saved': [warnings: string[]];
}>();

const { t } = useI18n();
const MIN_DURATION = 5;
const DEFAULT_DURATION = 60;
const titleFilter = useTableFilter(['title']);

const patientId = ref<number | null>(null);
const patientAddressId = ref<number | null>(null);
const fixedMode = ref(false);
const fixedStartTime = ref('');
const timeWindowStart = ref('');
const timeWindowEnd = ref('');
const durationMinutes = ref<number>(DEFAULT_DURATION);
const skillId = ref<number | null>(null);
const prioritized = ref(false);
const busy = ref(false);
const errorText = ref('');
// The visit was created but placing it hit a blocked professional: the confirm dialog
// decides between retrying with the override or leaving the visit pending.
const blockedPrompt = ref<(BlockedProfessionalPayload & { visitId: number }) | null>(null);

const placingCaregiverName = computed(() => props.routePlan.routePlanCaregivers
  .find(rpc => rpc.caregiverId === props.prefillCaregiverId)?.caregiverFullName ?? null);

// Only auto-place while the form is still a fixed-time visit (the user may switch to a window,
// in which case it just goes to the pending pool as usual).
const willPlaceDirectly = computed(
  () => props.prefillCaregiverId !== null && fixedMode.value && !!fixedStartTime.value,
);

const subtitle = computed(() => (willPlaceDirectly.value && placingCaregiverName.value ?
  t('routePlan.addVisit.subtitleAt', { name: placingCaregiverName.value }) :
  t('routePlan.addVisit.subtitle')));

const groupIds = computed(() => (props.routePlan.groupId ? [props.routePlan.groupId] : undefined));

const { data: patients } = useQuery({
  queryKey: ['patientsActive', groupIds],
  queryFn: () => patientsApi.index('active', groupIds.value),
  enabled: computed(() => props.modelValue),
});

const { data: skills } = useQuery({
  queryKey: ['skills', computed(() => props.routePlan.career)],
  queryFn: () => skillsApi.index(props.routePlan.career),
  enabled: computed(() => props.modelValue),
});

const patientOptions = computed(() => (patients.value ?? []).map((p) => ({
  value: p.id, title: p.fullName,
})));
const skillOptions = computed(() => (skills.value ?? []).map((s) => ({ value: s.id, title: s.name })));

// The selected patient's addresses; primary preselected. Hidden when there is only one choice.
const patientAddresses = computed(
  () => (patients.value ?? []).find((p) => p.id === patientId.value)?.patientAddressesAttributes ?? [],
);
const addressOptions = computed(() => patientAddresses.value.map((address) => ({
  value: address.id, title: [address.label, address.address].filter(Boolean).join(' · '),
})));

watch(patientId, () => {
  patientAddressId.value = patientAddresses.value.find((a) => a.primary)?.id ??
    patientAddresses.value[0]?.id ?? null;
  prioritized.value = false;
});

// "Prioritaria" is strictly category-scoped: only offered while the selected patient's
// current category opted into prioritization.
const prioritizationAvailable = computed(() => (patients.value ?? [])
  .find((p) => p.id === patientId.value)
  ?.currentServiceAssignment?.categoryPrioritizationEnabled ?? false);

function reset() {
  // prioritized is cleared by the patientId watcher (it can only be true with a patient set).
  patientId.value = null;
  patientAddressId.value = null;
  fixedMode.value = !!props.prefillStartTime;
  fixedStartTime.value = props.prefillStartTime ?? '';
  timeWindowStart.value = '';
  timeWindowEnd.value = '';
  durationMinutes.value = DEFAULT_DURATION;
  skillId.value = null;
  errorText.value = '';
  blockedPrompt.value = null;
}

watch(() => props.modelValue, (open) => {
  if (open) reset();
}, { immediate: true });

// "Hasta" is the deadline for the visit to END, so the window must hold the duration.
const windowTooShort = computed(() => !fixedMode.value &&
  !!timeWindowStart.value && !!timeWindowEnd.value &&
  !windowFitsDuration(timeWindowStart.value, timeWindowEnd.value, durationMinutes.value));
const timingValid = computed(() => (fixedMode.value ?
  !!fixedStartTime.value :
  !!timeWindowStart.value && !!timeWindowEnd.value && !windowTooShort.value));
const canSave = computed(() => patientId.value !== null &&
  durationMinutes.value >= MIN_DURATION && timingValid.value);

function visitAttrs() {
  return {
    patientId: patientId.value!,
    patientAddressId: patientAddressId.value,
    durationMinutes: durationMinutes.value,
    skillId: skillId.value,
    prioritized: prioritized.value,
    fixedStartTime: fixedMode.value ? fixedStartTime.value : undefined,
    timeWindowStart: fixedMode.value ? undefined : timeWindowStart.value,
    timeWindowEnd: fixedMode.value ? undefined : timeWindowEnd.value,
  };
}

async function placeVisit(visitId: number, overrideBlocked: boolean): Promise<string[]> {
  const { warnings } = await routePlansApi.placeAssignment(props.routePlan.id, visitId, {
    caregiverId: props.prefillCaregiverId!,
    arrivalTime: `${props.routePlan.date} ${fixedStartTime.value}`,
    overrideBlocked,
  });

  return warnings;
}

// Returns null while awaiting the blocked-professional confirmation (dialog stays open).
async function placeCreated(createdVisitId: number): Promise<string[] | null> {
  try {
    return await placeVisit(createdVisitId, false);
  } catch (error) {
    const blocked = blockedProfessionalPayload(error);
    if (blocked) {
      blockedPrompt.value = { ...blocked, visitId: createdVisitId };

      return null;
    }

    // The visit exists but could not be placed (e.g. overlap): it stays pending.
    return [t('routePlan.addVisit.placementFailed')];
  }
}

function finish(warnings: string[]) {
  emit('saved', warnings);
  emit('update:modelValue', false);
}

async function onSave() {
  if (!canSave.value || busy.value) return;
  busy.value = true;
  errorText.value = '';

  try {
    const { createdVisitId } = await routePlansApi.createVisit(props.routePlan.id, visitAttrs());
    const warnings = willPlaceDirectly.value ? await placeCreated(createdVisitId) : [];
    if (warnings !== null) finish(warnings);
  } catch (error) {
    errorText.value = requestErrorMessage(error, t('routePlan.visitForm.saveError'));
  } finally {
    busy.value = false;
  }
}

async function onBlockedConfirm() {
  const pending = blockedPrompt.value;
  if (!pending || busy.value) return;
  busy.value = true;

  try {
    finish(await placeVisit(pending.visitId, true));
  } catch {
    finish([t('routePlan.addVisit.placementFailed')]);
  } finally {
    blockedPrompt.value = null;
    busy.value = false;
  }
}

// Declining keeps the current semantics: the visit was created and stays pending.
function onBlockedCancel() {
  blockedPrompt.value = null;
  finish([t('routePlan.addVisit.placementFailed')]);
}
</script>

<template>
  <responsive-dialog
    :model-value="modelValue"
    max-width="600"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <v-card class="pa-2">
      <v-card-title class="d-flex align-center ga-3">
        <span>{{ t('routePlan.addVisit.title') }}</span>
        <v-spacer />
        <v-btn
          icon="mdi-close"
          variant="text"
          size="small"
          :aria-label="t('routePlan.cancel')"
          @click="emit('update:modelValue', false)"
        />
      </v-card-title>

      <v-card-text>
        <v-alert
          type="info"
          variant="tonal"
          density="compact"
          class="mb-6"
        >
          {{ subtitle }}
        </v-alert>

        <!-- Who -->
        <v-autocomplete
          v-model="patientId"
          :items="patientOptions"
          :custom-filter="titleFilter"
          :label="t('routePlan.patient')"
          variant="outlined"
          density="comfortable"
          hide-details="auto"
        />
        <v-select
          v-if="addressOptions.length > 1"
          v-model="patientAddressId"
          :items="addressOptions"
          :label="t('routePlan.visitAddress')"
          variant="outlined"
          density="comfortable"
          hide-details="auto"
          class="mt-4"
        />

        <!-- When -->
        <div class="text-subtitle-2 font-weight-medium mt-6">
          {{ t('routePlan.addVisit.timing') }}
        </div>
        <p class="text-body-2 text-medium-emphasis mb-3">
          {{ fixedMode ? t('routePlan.addVisit.fixedHint') : t('routePlan.addVisit.windowHint') }}
        </p>
        <v-btn-toggle
          v-model="fixedMode"
          mandatory
          density="comfortable"
          color="primary"
          variant="outlined"
          divided
          class="mb-4 d-flex"
        >
          <v-btn
            :value="false"
            class="flex-grow-1"
          >
            {{ t('routePlan.addVisit.window') }}
          </v-btn>
          <v-btn
            :value="true"
            class="flex-grow-1"
          >
            {{ t('routePlan.fixedStartTime') }}
          </v-btn>
        </v-btn-toggle>

        <v-row
          v-if="fixedMode"
          class="mt-0"
        >
          <v-col
            cols="12"
            sm="6"
          >
            <time-input
              v-model="fixedStartTime"
              density="comfortable"
              :label="t('routePlan.fixedStartTime')"
            />
          </v-col>
        </v-row>
        <v-row
          v-else
          class="mt-0"
        >
          <v-col
            cols="12"
            sm="6"
          >
            <time-input
              v-model="timeWindowStart"
              density="comfortable"
              :label="t('routePlan.timeWindowStart')"
            />
          </v-col>
          <v-col
            cols="12"
            sm="6"
          >
            <time-input
              v-model="timeWindowEnd"
              density="comfortable"
              :label="t('routePlan.timeWindowEnd')"
              :title="t('routePlan.timeWindowEndHint')"
              :error-messages="windowTooShort ?
                t('routePlan.validation.windowTooShort') : undefined"
            />
          </v-col>
        </v-row>

        <!-- Details -->
        <div class="text-subtitle-2 font-weight-medium mt-4 mb-3">
          {{ t('routePlan.addVisit.details') }}
        </div>
        <v-row class="mt-0">
          <v-col
            cols="12"
            sm="6"
          >
            <v-text-field
              v-model.number="durationMinutes"
              type="number"
              :label="t('routePlan.durationMinutes')"
              :suffix="t('routePlan.minutes')"
              :min="MIN_DURATION"
              variant="outlined"
              density="comfortable"
              hide-details="auto"
            />
          </v-col>
          <v-col
            v-if="skillOptions.length > 0"
            cols="12"
            sm="6"
          >
            <v-select
              v-model="skillId"
              :items="skillOptions"
              :label="t('routePlan.requiredSkill')"
              :placeholder="t('routePlan.noRequiredSkill')"
              clearable
              variant="outlined"
              density="comfortable"
              hide-details="auto"
            />
          </v-col>
          <v-col
            v-if="prioritizationAvailable"
            cols="12"
          >
            <v-switch
              v-model="prioritized"
              :label="t('routePlan.prioritizedShort')"
              :hint="t('routePlan.prioritizedHint')"
              persistent-hint
              density="compact"
              color="primary"
            />
          </v-col>
        </v-row>

        <v-alert
          v-if="errorText"
          type="error"
          variant="tonal"
          density="compact"
          class="mt-4"
        >
          {{ errorText }}
        </v-alert>
      </v-card-text>

      <v-card-actions class="px-4 pb-3 pt-0">
        <v-spacer />
        <v-btn
          color="primary"
          variant="flat"
          :loading="busy"
          :disabled="!canSave"
          @click="onSave"
        >
          {{ t('routePlan.addVisit.save') }}
        </v-btn>
      </v-card-actions>
    </v-card>

    <route-blocked-confirm-dialog
      :model-value="blockedPrompt !== null"
      :caregiver-name="blockedPrompt?.caregiverName ?? ''"
      :patient-name="blockedPrompt?.patientName ?? ''"
      :busy="busy"
      @confirm="onBlockedConfirm"
      @cancel="onBlockedCancel"
    />
  </responsive-dialog>
</template>
